/**
 * Types shared between the Worker API and the React client.
 * Nothing in here may import from `worker/` or `client/`.
 */

export const GUEST_EMAIL_SUFFIX = '@guest.heresmytrip.test';

/** The one account allowed to see /admin. The Worker enforces this; the client only uses it to decide whether to render the nav link. */
export const ADMIN_EMAIL = 'jagiddens@gmail.com';

export interface AdminStats {
  users: {
    named: number;
    guests: number;
    namedLast7Days: number;
    namedLast30Days: number;
  };
  trips: {
    total: number;
    last7Days: number;
    withItems: number;
  };
  items: { total: number };
  wishlist: { saves: number };
  shareLinks: {
    total: number;
    revoked: number;
    totalOpens: number;
  };
  /** All-time event counts by name (trip_created, share_link_opened, ...). */
  eventTotals: Record<string, number>;
  /** Last 14 days of key events: { date: 'YYYY-MM-DD', name, count }. */
  daily: { date: string; name: string; count: number }[];
  recentTrips: {
    id: string;
    title: string;
    slug: string;
    createdAt: number;
    ownerEmail: string;
    itemCount: number;
  }[];
  recentSignups: { email: string; name: string | null; createdAt: number }[];
}

export const ITEM_TYPES = ['lodging', 'restaurant', 'activity', 'tip'] as const;
export type ItemType = (typeof ITEM_TYPES)[number];

/** Tips carry no image and render as a bulleted list rather than a card. */
export const TYPES_WITHOUT_IMAGES: readonly ItemType[] = ['tip'];

export const ITEM_TYPE_LABELS: Record<ItemType, { singular: string; plural: string }> = {
  lodging: { singular: 'Where we stayed', plural: 'Where we stayed' },
  restaurant: { singular: 'Where we ate', plural: 'Where we ate' },
  activity: { singular: 'What we did', plural: 'What we did' },
  tip: { singular: 'Worth knowing', plural: 'Worth knowing' },
};

export type Visibility = 'private' | 'shared' | 'link';
export type UnfurlStatus = 'none' | 'pending' | 'done' | 'failed';

export interface User {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
}

/** The subset of a user safe to expose on a public trip page. */
export interface PublicUser {
  id: string;
  name: string | null;
  avatarUrl: string | null;
}

export interface TripItem {
  id: string;
  tripId: string;
  type: ItemType;
  url: string | null;
  ogTitle: string | null;
  ogImage: string | null;
  ogDescription: string | null;
  ogSiteName: string | null;
  note: string | null;
  sortOrder: number;
  lat: number | null;
  lng: number | null;
  unfurlStatus: UnfurlStatus;
  /** Google Places attachment — powers the photo and the "Open in Maps" link. */
  placeId: string | null;
  placeName: string | null;
  placeAddress: string | null;
  mapUrl: string | null;
  imageSource: ImageSource | null;
}

export type ImageSource = 'unfurl' | 'places' | 'manual';

export interface PlaceCandidate {
  placeId: string;
  name: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  mapUrl: string;
  photoUrl: string | null;
  rating: number | null;
  websiteUrl: string | null;
}

export interface Trip {
  id: string;
  ownerId: string;
  title: string;
  destination: string | null;
  slug: string;
  startDate: string | null;
  endDate: string | null;
  coverImage: string | null;
  notes: string | null;
  visibility: Visibility;
  clonedFromTripId: string | null;
  createdAt: number;
  updatedAt: number;
}

/** A trip plus everything a trip page needs to render in one request. */
export interface TripWithItems extends Trip {
  owner: PublicUser;
  items: TripItem[];
  /** Present for the viewer's own relationship to this trip. */
  viewer: ViewerContext;
}

export interface ViewerContext {
  isAuthenticated: boolean;
  isOwner: boolean;
  hasSaved: boolean;
  canEdit: boolean;
  /**
   * Phase 1 ships ungated: this is false, so Save/Clone act immediately and
   * the gate is only logged. Flipping AUTH_GATE_ENABLED makes it true for
   * anonymous viewers, which is what the client keys the auth prompt off.
   */
  gateRequired: boolean;
}

export interface ShareLink {
  token: string;
  tripId: string;
  url: string;
  createdAt: number;
  expiresAt: number | null;
  maxUses: number | null;
  useCount: number;
  revoked: boolean;
}

export interface UnfurlResult {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  siteName: string | null;
  favicon: string | null;
  status: Extract<UnfurlStatus, 'done' | 'failed'>;
}

export const EVENT_NAMES = [
  'share_link_created',
  'share_link_opened',
  'gate_shown',
  'signup_from_share',
  'trip_saved',
  'trip_cloned',
  'trip_created',
  'items_imported',
  'chat_turn',
] as const;
export type EventName = (typeof EVENT_NAMES)[number];

export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
}
